import { Controller } from "@hotwired/stimulus"

// Drives the agent chat transcript: keeps the scroll anchored to the
// newest message as Turbo Stream broadcasts append/replace bubbles, and
// clears the composer after submit.
//
// Anchoring is conditional: broadcasts only pull the scroll down when
// the reader was already at (or near) the bottom. Scrolling up to
// re-read history opts out until the reader returns to the bottom or
// sends a message.
export default class extends Controller {
  static targets = ["messages", "input", "queue"]

  ANCHOR_THRESHOLD_PX = 80

  connect() {
    this.scrollToBottom()
    this.observer = new MutationObserver(() => {
      if (this.anchored) this.scrollToBottom()
    })
    this.observer.observe(this.messagesTarget, { childList: true, subtree: true })

    // The queue frame collapses to zero height when it holds no bubbles.
    // childElementCount (not :empty) so Turbo's whitespace text nodes don't
    // keep it open.
    if (this.hasQueueTarget) {
      this.syncQueueVisibility()
      this.queueObserver = new MutationObserver(() => this.syncQueueVisibility())
      this.queueObserver.observe(this.queueTarget, { childList: true })
    }

    this.anchored = true
    this.onScroll = () => {
      const el = this.messagesTarget
      this.anchored = el.scrollHeight - el.scrollTop - el.clientHeight < this.ANCHOR_THRESHOLD_PX
    }
    this.messagesTarget.addEventListener("scroll", this.onScroll, { passive: true })
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.queueObserver) this.queueObserver.disconnect()
    if (this.onScroll) this.messagesTarget.removeEventListener("scroll", this.onScroll)
  }

  // Submit on Enter (Shift+Enter inserts a newline).
  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      event.target.form.requestSubmit()
    }
  }

  clearInput() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus()
    }
    // Sending a message re-anchors — you always want to see the reply.
    this.anchored = true
    this.scrollToBottom()
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  syncQueueVisibility() {
    this.queueTarget.classList.toggle("hidden", this.queueTarget.childElementCount === 0)
  }
}
